import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  FileText,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
} from 'lucide-react';
import { getRequestById, cancelRequest } from '../../../services/studentRequestsService';
import { StudentRequest, RequestStatus } from '../../../types/student-requests.types';
import ConfirmDialog from '@/shared/components/ConfirmDialog';

const RequestDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [request, setRequest] = useState<StudentRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ isOpen: boolean; title: string; message: string }>({
    isOpen: false,
    title: '',
    message: '',
  });
  const [errorDialog, setErrorDialog] = useState<{ isOpen: boolean; title: string; message: string }>({
    isOpen: false,
    title: '',
    message: '',
  });
  const navigate = useNavigate();
  const location = useLocation();

  // Extract the requests list path from current URL
  const getRequestsPath = () => {
    const pathParts = location.pathname.split('/');
    const requestsIndex = pathParts.findIndex(part => part === 'requests');
    if (requestsIndex !== -1) {
      return pathParts.slice(0, requestsIndex + 1).join('/');
    }
    return '/student/requests';
  };

  useEffect(() => {
    if (id) {
      loadRequest();
    }
  }, [id]);

  const loadRequest = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await getRequestById(id);
      setRequest(data);
    } catch (error) {
      console.error('Error loading request:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    setConfirmDialog({
      isOpen: true,
      title: 'Confirmer l\'annulation',
      message: 'Êtes-vous sûr de vouloir annuler cette demande ? Cette action est irréversible.',
    });
  };

  const handleConfirmCancel = async () => {
    if (!id) return;

    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      setCancelling(true);
      await cancelRequest(id);
      navigate(getRequestsPath());
    } catch (error: any) {
      setErrorDialog({
        isOpen: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de l\'annulation',
      });
      setCancelling(false);
    }
  };

  const getStatusIcon = (status: RequestStatus) => {
    const statusIcons: Record<RequestStatus, typeof Clock> = {
      [RequestStatus.DRAFT]: Clock,
      [RequestStatus.PENDING_FINANCE]: Clock,
      [RequestStatus.PENDING_ACADEMIC]: Clock,
      [RequestStatus.FINANCE_APPROVED]: CheckCircle,
      [RequestStatus.ACADEMIC_APPROVED]: CheckCircle,
      [RequestStatus.APPROVED]: CheckCircle,
      [RequestStatus.GENERATED]: CheckCircle,
      [RequestStatus.DOWNLOADED_FOR_PRINT]: CheckCircle,
      [RequestStatus.PRINTED]: CheckCircle,
      [RequestStatus.DELIVERED]: CheckCircle,
      [RequestStatus.REJECTED_FINANCE]: XCircle,
      [RequestStatus.REJECTED_ACADEMIC]: XCircle,
      [RequestStatus.CANCELLED]: XCircle,
    };
    return statusIcons[status] || AlertCircle;
  };

  const getStatusColor = (status: RequestStatus) => {
    if (status.includes('REJECTED') || status === RequestStatus.CANCELLED) {
      return 'text-red-600 bg-red-100';
    }
    if (status.includes('APPROVED') || status === RequestStatus.DELIVERED) {
      return 'text-green-600 bg-green-100';
    }
    return 'text-yellow-600 bg-yellow-100';
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      PRE_INSCRIPTION: 'Attestation d\'Inscription',
      ATTESTATION_SCOLARITE: 'Attestation de Scolarité',
      RELEVE_NOTES_ANNUEL: 'Relevé de Notes Annuel',
      ATTESTATION_REUSSITE: 'Attestation de Réussite',
      CONVENTION_STAGE: 'Convention de Stage',
      ATTESTATION_INSCRIPTION: 'Attestation d\'Inscription',
      CERTIFICAT_ASSIDUITE: 'Certificat d\'Assiduité',
      RELEVE_NOTES_SEMESTRIEL: 'Relevé de Notes Semestriel',
      ATTESTATION_STAGE: 'Attestation de Stage',
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: RequestStatus) => {
    // Simplified display
    if (status === RequestStatus.APPROVED) {
      return 'Approuvée';
    }
    if ([RequestStatus.GENERATED, RequestStatus.DOWNLOADED_FOR_PRINT, RequestStatus.PRINTED].includes(status)) {
      return 'Prête à collecter';
    }
    if (status === RequestStatus.DELIVERED) {
      return 'Délivrée';
    }

    // Other statuses
    const labels: Record<RequestStatus, string> = {
      [RequestStatus.DRAFT]: 'Brouillon',
      [RequestStatus.PENDING_FINANCE]: 'En attente - Finance',
      [RequestStatus.PENDING_ACADEMIC]: 'En attente - Académique',
      [RequestStatus.FINANCE_APPROVED]: 'Finance approuvée',
      [RequestStatus.ACADEMIC_APPROVED]: 'Académique approuvée',
      [RequestStatus.APPROVED]: 'Approuvée',
      [RequestStatus.GENERATED]: 'Générée',
      [RequestStatus.DOWNLOADED_FOR_PRINT]: 'Téléchargée',
      [RequestStatus.PRINTED]: 'Imprimée',
      [RequestStatus.DELIVERED]: 'Délivrée',
      [RequestStatus.REJECTED_FINANCE]: 'Rejetée - Finance',
      [RequestStatus.REJECTED_ACADEMIC]: 'Rejetée - Académique',
      [RequestStatus.CANCELLED]: 'Annulée',
    };

    return labels[status] || status;
  };

  /** French label of a timeline step (a system step has no validator). */
  const getTimelineLabel = (status: RequestStatus) => {
    const labels: Record<RequestStatus, string> = {
      [RequestStatus.DRAFT]: 'Brouillon',
      [RequestStatus.PENDING_FINANCE]: 'Demande soumise — en attente de validation financière',
      [RequestStatus.PENDING_ACADEMIC]: 'En attente de validation académique',
      [RequestStatus.FINANCE_APPROVED]: 'Validation financière acquise',
      [RequestStatus.ACADEMIC_APPROVED]: 'Validation académique acquise',
      [RequestStatus.APPROVED]: 'Demande approuvée',
      [RequestStatus.GENERATED]: 'Document prêt à retirer',
      [RequestStatus.DOWNLOADED_FOR_PRINT]: 'Document en cours d\'impression',
      [RequestStatus.PRINTED]: 'Document imprimé et signé',
      [RequestStatus.DELIVERED]: 'Document remis',
      [RequestStatus.REJECTED_FINANCE]: 'Refusée par le service financier',
      [RequestStatus.REJECTED_ACADEMIC]: 'Refusée par la direction pédagogique',
      [RequestStatus.CANCELLED]: 'Demande annulée',
    };
    return labels[status] || status;
  };

  const canCancel = (status: RequestStatus) => {
    return [
      RequestStatus.PENDING_FINANCE,
      RequestStatus.PENDING_ACADEMIC,
      RequestStatus.FINANCE_APPROVED,
      RequestStatus.ACADEMIC_APPROVED,
    ].includes(status);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-4">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Demande non trouvée</h3>
          <button
            onClick={() => navigate(getRequestsPath())}
            className="text-blue-600 hover:text-blue-700"
          >
            Retour à mes demandes
          </button>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(request.status);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(getRequestsPath())}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour à mes demandes
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{getTypeLabel(request.requestType)}</h1>
        <p className="text-gray-600 mt-1">Référence: {request.referenceNumber}</p>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-full ${getStatusColor(request.status)}`}>
              <StatusIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Statut actuel</h2>
              <p className="text-gray-600">{getStatusLabel(request.status)}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Créée le</p>
            <p className="font-medium text-gray-900">
              {new Date(request.createdAt).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Request Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations de la demande</h2>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-gray-700">
            <FileText className="w-5 h-5 text-gray-400" />
            <span className="font-medium">Type:</span>
            <span>{getTypeLabel(request.requestType)}</span>
          </div>
          <div className="flex items-center gap-3 text-gray-700">
            <Calendar className="w-5 h-5 text-gray-400" />
            <span className="font-medium">Année académique:</span>
            <span>{request.academicYear?.name}</span>
          </div>
          {request.purpose && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-1">Motif de la demande:</p>
              <p className="text-gray-600 italic">"{request.purpose}"</p>
            </div>
          )}
        </div>
      </div>

      {/* Validation Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Validations</h2>
        <div className="space-y-4">
          {/* Finance Validation */}
          <div className="flex items-start gap-3">
            {request.financeValidatedAt ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            ) : request.financeRejectionReason ? (
              <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
            ) : (
              <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
            )}
            <div className="flex-1">
              <p className="font-medium text-gray-900">Validation Financière</p>
              {request.financeAutoApproved && (
                <p className="text-sm text-green-600">✓ Validée automatiquement</p>
              )}
              {request.financeValidatedAt && !request.financeAutoApproved && (
                <p className="text-sm text-gray-600">
                  Validée le {new Date(request.financeValidatedAt).toLocaleDateString('fr-FR')}
                  {request.financeValidator && (
                    <span className="ml-1">
                      par {request.financeValidator.firstName} {request.financeValidator.lastName}
                    </span>
                  )}
                </p>
              )}
              {request.financeRejectionReason && (
                <p className="text-sm text-red-600 mt-1">
                  Rejetée: {request.financeRejectionReason}
                </p>
              )}
              {!request.financeValidatedAt && !request.financeRejectionReason && (
                <p className="text-sm text-gray-600">En attente de validation</p>
              )}
            </div>
          </div>

          {/* Academic Validation */}
          <div className="flex items-start gap-3">
            {request.academicValidatedAt ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            ) : request.academicRejectionReason ? (
              <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
            ) : (
              <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
            )}
            <div className="flex-1">
              <p className="font-medium text-gray-900">Validation Académique</p>
              {request.academicAutoApproved && (
                <p className="text-sm text-green-600">✓ Validée automatiquement</p>
              )}
              {request.academicValidatedAt && !request.academicAutoApproved && (
                <p className="text-sm text-gray-600">
                  Validée le {new Date(request.academicValidatedAt).toLocaleDateString('fr-FR')}
                  {request.academicValidator && (
                    <span className="ml-1">
                      par {request.academicValidator.firstName} {request.academicValidator.lastName}
                    </span>
                  )}
                </p>
              )}
              {request.academicRejectionReason && (
                <p className="text-sm text-red-600 mt-1">
                  Rejetée: {request.academicRejectionReason}
                </p>
              )}
              {!request.academicValidatedAt && !request.academicRejectionReason && (
                <p className="text-sm text-gray-600">En attente de validation</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {request.statusHistory && request.statusHistory.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Historique</h2>
          <div className="space-y-4">
            {request.statusHistory.map((item, index) => (
              <div key={item.id} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                  </div>
                  {index < request.statusHistory!.length - 1 && (
                    <div className="w-0.5 flex-1 bg-gray-200 my-1"></div>
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-gray-900">
                      {getTimelineLabel(item.toStatus)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(item.timestamp).toLocaleString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  {item.changedByUser && (
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {item.changedByUser.firstName} {item.changedByUser.lastName} (
                      {item.changedByUser.role})
                    </p>
                  )}
                  {item.comment && (
                    <p className="text-sm text-gray-600 mt-1 italic">"{item.comment}"</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end gap-4">
        {canCancel(request.status) && (
          <button
            onClick={handleCancelClick}
            disabled={cancelling}
            className="px-6 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition disabled:opacity-50"
          >
            {cancelling ? 'Annulation...' : 'Annuler la demande'}
          </button>
        )}
      </div>

      {/* Confirm Cancel Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Oui, annuler"
        cancelText="Non, garder"
        onConfirm={handleConfirmCancel}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        variant="danger"
      />

      {/* Error Dialog */}
      <ConfirmDialog
        isOpen={errorDialog.isOpen}
        title={errorDialog.title}
        message={errorDialog.message}
        confirmText="OK"
        cancelText="Fermer"
        onConfirm={() => setErrorDialog({ ...errorDialog, isOpen: false })}
        onCancel={() => setErrorDialog({ ...errorDialog, isOpen: false })}
        variant="warning"
      />
    </div>
  );
};

export default RequestDetailPage;
